// StudentSpellCheck.tsx
import { useState, useEffect, useRef, useMemo } from 'react';
import FireworksLayer from '@/components/interAction/FireworksLayer';
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import FlipCard from '@/components/interAction/FlipCard'
import RecordingBox from '@/components/atoms/RecordingBox/RecordingBox'
import DeskImg from '@/assets/img/desk_test.png'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import * as dragStyles from '@/components/interAction/Drag/DragStyle.css';
import clsx from 'clsx';
import { getStudentActivityTitle } from '@/utils/activityTitle';

const toBoolean = (value: any): boolean => {
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (!normalized) return false;
        if (['false', '0', 'n', 'no'].includes(normalized)) return false;
        return ['y', 'yes', 'true', '1'].includes(normalized);
    }
    return Boolean(value);
};

interface WordChoiceProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
}

function shuffleArray<T>(array: T[]): T[] {
    const arr = [...array]
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  }

export default function StudentSpellCheck({ activity, unitData, onResponse, existingResponse, isHomework = false }: WordChoiceProps) {
    const [pool, setPool] = useState<string[]>([]);
    const [answer, setAnswer] = useState<string[]>([]);
    const isInitialMount = useRef(true); // 초기 마운트 여부 추적
    const [shakeWordId, setShakeWordId] = useState<string | null>(null);
    
    // configJson에서 recordingEnabled 확인
    let config: any = {};
    try {
        if (activity?.configJson) {
            config = typeof activity.configJson === 'string' 
                ? JSON.parse(activity.configJson) 
                : activity.configJson;
        }
    } catch (e) {
        console.error('configJson 파싱 에러:', e);
    }
    
    const recordingEnabled = toBoolean(config.recordingEnabled);
    
    // activity.items에서 실제 데이터 가져오기
    const activityData = activity?.items?.[0] || {};
    let content: any = {};
    
    try {
        // contentJson이 문자열인 경우 파싱, 이미 객체인 경우 그대로 사용
        if (typeof activityData.contentJson === 'string') {
            content = JSON.parse(activityData.contentJson);
        } else if (typeof activityData.contentJson === 'object') {
            content = activityData.contentJson;
        }
    } catch (e) {
        console.error('contentJson 파싱 에러:', e);
    }

    // DB의 contentJson 구조: { "word": "문장 전체", "wordImageUrl": "이미지URL" }
    const originalSentence = content.word || content.sentence || content.text || 'a book on the desk';
    const imageUrl = content.wordImageUrl || content.imageUrl || content.image;
    const audioUrl = content.audioUrl || content.audio || null;
    const title = getStudentActivityTitle(activity?.title, '단어 선택해보세요!');

    console.log('=== WordChoice 데이터 ===');
    console.log('Activity:', activity);
    console.log('activityData:', activityData);
    console.log('Content:', content);
    console.log('Sentence (from content.word):', originalSentence);
    console.log('ImageUrl:', imageUrl);
    console.log('AudioUrl:', audioUrl);

    const [isReady, setIsReady] = useState(false);
    const [showFireworks, setShowFireworks] = useState(false);
    const [flipStatus, setFlipStatus] = useState<'idle' | 'success' | 'fail'>('idle');
    const shouldGateFlow = !isHomework && (recordingEnabled || Boolean(audioUrl));
    const gatedFlowActive = shouldGateFlow;
    const initialHasListened = !gatedFlowActive || !audioUrl || Boolean(existingResponse?.hasListened);
    const initialHasRecorded = recordingEnabled
        ? Boolean(existingResponse?.hasRecorded || existingResponse?.recordingUrl)
        : (!gatedFlowActive || Boolean(existingResponse?.hasRecorded || existingResponse?.recordingUrl));
    const [hasListened, setHasListened] = useState<boolean>(initialHasListened);
    const [hasRecorded, setHasRecorded] = useState<boolean>(initialHasRecorded);
    const [isProcessingWrong, setIsProcessingWrong] = useState(false);
    const canRecord = recordingEnabled && (!gatedFlowActive || hasListened);
    const unitCompletionStatus = (() => {
        if (!unitData) return false;
        const status = typeof unitData.status === 'string' ? unitData.status.toUpperCase() : '';
        const progressStatus = typeof unitData.progressStatus === 'string' ? unitData.progressStatus.toUpperCase() : '';
        const completedFlag = Boolean(unitData.completed || unitData.wasCompleted);
        return completedFlag || status === 'COMPLETED' || progressStatus === 'COMPLETED';
    })();
    const isLocked = !isHomework && (existingResponse?.isCorrect === true || unitCompletionStatus);
    // canArrange: 게이트 플로우가 없거나, 필요한 조건을 만족해야 함
    // - 녹음 활성화: 듣기 완료 && 녹음 완료
    // - 녹음 비활성화: 듣기 완료만
    const canArrange = (!gatedFlowActive || (hasListened && (recordingEnabled ? hasRecorded : true))) && !isProcessingWrong;
    const allowArrangement = canArrange && !isLocked;
    
    // 디버깅용 로그
    useEffect(() => {
        console.log('=== allowArrangement 디버깅 ===');
        console.log('gatedFlowActive:', gatedFlowActive);
        console.log('hasRecorded:', hasRecorded);
        console.log('isProcessingWrong:', isProcessingWrong);
        console.log('isLocked:', isLocked);
        console.log('isHomework:', isHomework);
        console.log('existingResponse?.isCorrect:', existingResponse?.isCorrect);
        console.log('unitCompletionStatus:', unitCompletionStatus);
        console.log('canArrange:', canArrange);
        console.log('allowArrangement:', allowArrangement);
    }, [gatedFlowActive, hasRecorded, isProcessingWrong, isLocked, isHomework, existingResponse?.isCorrect, unitCompletionStatus, canArrange, allowArrangement]);

    const computeHomeworkReady = (sentence: string, recordedFlag?: boolean, poolSizeOverride?: number) => {
        const trimmed = sentence.trim();
        const recordedSatisfied = !recordingEnabled || (typeof recordedFlag === 'boolean' ? recordedFlag : hasRecorded);
        const poolSize = typeof poolSizeOverride === 'number' ? poolSizeOverride : pool.length;
        const allWordsPlaced = poolSize === 0 && trimmed.length > 0;
        return allWordsPlaced && recordedSatisfied;
    };

    const handleRecordingComplete = (audioBlob: Blob, audioUrl: string) => {
        console.log('녹음 완료:', audioBlob, audioUrl);
        const currentSentence = convertIdsToSentence(answer);
        const isCorrectNow = currentSentence === originalSentence && pool.length === 0;
        const nextHasRecorded = true;
        const nextHasListened = true;
        setHasRecorded(nextHasRecorded);
        setHasListened(nextHasListened);
        
        if (onResponse) {
            onResponse({ 
                ...existingResponse,
                recordingBlob: audioBlob, 
                recordingUrl: audioUrl,
                answer: currentSentence,
                isCorrect: isCorrectNow,
                ...(gatedFlowActive && { hasRecorded: nextHasRecorded, hasListened: nextHasListened }),
                isHomeworkReady: computeHomeworkReady(currentSentence, nextHasRecorded, pool.length)
            });
        }
    };

    const wordTokens = originalSentence.trim().split(' ').filter((w: string) => w.length > 0);
    const wordItems = wordTokens.map((word: string, index: number): { id: string; label: string } => ({
        id: `${index}-${word}`,
        label: word,
    }));
    const wordMap = useMemo(() => {
        const map = new Map<string, string>();
        wordItems.forEach(({ id, label }: { id: string; label: string }) => map.set(id, label));
        return map;
    }, [originalSentence]);
    const labelToIdsMap = useMemo(() => {
        const map = new Map<string, string[]>();
        wordItems.forEach(({ id, label }: { id: string; label: string }) => {
            const arr = map.get(label) || [];
            arr.push(id);
            map.set(label, arr);
        });
        return map;
    }, [originalSentence]);
    const convertSentenceToIds = (sentence: string) => {
        const tokens = sentence.trim().split(' ').filter((w) => w.length > 0);
        const usage = new Map<string, number>();
        return tokens
            .map((token) => {
                const ids = labelToIdsMap.get(token);
                if (!ids || ids.length === 0) return null;
                const used = usage.get(token) || 0;
                usage.set(token, used + 1);
                return ids[used] ?? null;
            })
            .filter((id): id is string => Boolean(id));
    };
    const convertIdsToSentence = (ids: string[]) =>
        ids
            .map((id) => wordMap.get(id) || '')
            .filter((word) => word.length > 0)
            .join(' ');
    const correctWordIds = wordItems.map((item: { id: string; label: string }) => item.id);

    // 컴포넌트 마운트 시 기존 응답 데이터 즉시 저장
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (existingResponse && Object.keys(existingResponse).length > 0) {
            console.log('WordChoice: 기존 응답 데이터 복원 및 저장');
            if (onResponse) {
                const existingSentence = existingResponse.answer || '';
                const recordedFlag = existingResponse.hasRecorded || existingResponse.recordingUrl ? true : hasRecorded;
                onResponse({
                    ...existingResponse,
                    isHomeworkReady: computeHomeworkReady(existingSentence, recordedFlag, pool.length)
                });
            }
        }
    }, []);

    useEffect(() => {
        const words = wordItems.map((item: { id: string; label: string }) => item.id);
        
        // existingResponse가 있으면 이전 답변 복원
        if (existingResponse?.answer && existingResponse.answer.trim().length > 0) {
            console.log('=== WordChoice 상태 복원 ===');
            console.log('existingResponse:', existingResponse);
            
            const answerIds = convertSentenceToIds(existingResponse.answer);
            const usedSet = new Set(answerIds);
            const remainingWords = words.filter((wordId: string) => !usedSet.has(wordId));
            
            setAnswer(answerIds);
            setPool(remainingWords);
            console.log('복원된 answer:', answerIds);
            console.log('복원된 pool:', remainingWords);
        } else {
            // 새로운 문제인 경우 섞어서 설정
            console.log('=== WordChoice 새로운 문제 ===');
            const shuffled = shuffleArray<string>(words);
            setPool(shuffled);
            setAnswer([]);
        }
        
        setIsReady(true);
    }, [originalSentence]);

    useEffect(() => {
        if (!gatedFlowActive || !audioUrl) {
            setHasListened(true);
            return;
        }
        if (existingResponse?.hasListened) {
            setHasListened(true);
        }
    }, [gatedFlowActive, audioUrl, existingResponse?.hasListened]);

    useEffect(() => {
        if (!gatedFlowActive) {
            if (recordingEnabled) {
                const alreadyRecorded = Boolean(existingResponse?.hasRecorded || existingResponse?.recordingUrl);
                setHasRecorded(alreadyRecorded);
            } else {
                setHasRecorded(true);
            }
            return;
        }
        if (existingResponse?.hasRecorded || existingResponse?.recordingUrl) {
            setHasRecorded(true);
        }
    }, [gatedFlowActive, recordingEnabled, existingResponse?.hasRecorded, existingResponse?.recordingUrl]);

    const handleWordClick = (wordId: string, fromPool: boolean) => {
        // allowArrangement가 false면 클릭 무시 (이미 disabled이지만 이중 체크)
        if (!allowArrangement) {
            console.log('handleWordClick: allowArrangement가 false입니다');
            return;
        }

        let updatedAnswer = [...answer];
        let updatedPool = [...pool];

        if (fromPool) {
            // Pool에서 Answer로 이동 (순서대로 추가)
            updatedPool = pool.filter((id) => id !== wordId);
            if (!updatedAnswer.includes(wordId)) {
                updatedAnswer = [...updatedAnswer, wordId];
            }
            
            // UI 업데이트 (일단 이동시킴)
            setPool(updatedPool);
            setAnswer(updatedAnswer);

            if (!isHomework) {
                const targetIndex = updatedAnswer.length - 1;
                const expectedWordId = correctWordIds[targetIndex];
                
                if (wordId !== expectedWordId) {
                    // 오답 처리
                    setIsProcessingWrong(true);
                    setShakeWordId(wordId);

                    // 1초 후 원복
                    setTimeout(() => {
                        setAnswer(prev => prev.filter(id => id !== wordId));
                        setPool(prev => {
                            if (!prev.includes(wordId)) return [...prev, wordId];
                            return prev;
                        });
                        setShakeWordId(null);
                        setIsProcessingWrong(false);
                    }, 1000);

                    // 오답인 경우 저장하지 않고 리턴 (임시 상태이므로)
                    return;
                }
            }
        } else {
            // Answer에서 Pool로 이동
            updatedAnswer = answer.filter((id) => id !== wordId);
            if (!updatedPool.includes(wordId)) {
                updatedPool = [...updatedPool, wordId];
            }
            setAnswer(updatedAnswer);
            setPool(updatedPool);
        }

        // 중간 상태 저장 (녹음 정보도 함께 유지)
        if (onResponse) {
            const currentSentence = convertIdsToSentence(updatedAnswer);
            const isCorrect = currentSentence === originalSentence && updatedPool.length === 0;
            console.log('WordChoice 저장:', { answer: currentSentence, isCorrect, poolLength: updatedPool.length });
            
            // 정답을 맞췄을 때 즉시 플립 카드 표시 (학습 모드에서만)
            if (isCorrect && !isHomework && updatedPool.length === 0) {
                setFlipStatus('success');
                setShowFireworks(true);
                setTimeout(() => {
                    setShowFireworks(false);
                    setFlipStatus('idle');
                }, 3000);
            }
            
            onResponse({ 
                ...existingResponse,
                answer: currentSentence,
                isCorrect: isCorrect,
                ...(gatedFlowActive && { hasListened, hasRecorded }),
                // 기존 녹음 정보가 있으면 유지
                ...(existingResponse?.recordingBlob && { recordingBlob: existingResponse.recordingBlob }),
                ...(existingResponse?.recordingUrl && { recordingUrl: existingResponse.recordingUrl }),
                isHomeworkReady: computeHomeworkReady(currentSentence, undefined, updatedPool.length)
            });
        }
    };

    useEffect(() => {
        if (!isReady) return;
        if (pool.length === 0 && answer.length > 0) {
            const finalSentence = convertIdsToSentence(answer);
            const isCorrect = finalSentence === originalSentence;
            
            // 초기 마운트 시 이미 완성된 답변으로 복원된 경우에만 애니메이션 스킵
            if (isInitialMount.current) {
                isInitialMount.current = false;
                // existingResponse가 있고 이미 정답을 맞춘 상태로 복원된 경우에만 애니메이션 스킵
                if (existingResponse?.answer && existingResponse.answer === originalSentence) {
                    return;
                }
            }
            
            if (isCorrect) {
                // 숙제가 아닐 때만 피드백 표시
                if (!isHomework) {
                    setFlipStatus('success');
                    setShowFireworks(true);
                }
                
                // 응답 저장
                if (onResponse) {
                    onResponse({ 
                        ...existingResponse,
                        answer: finalSentence, 
                        isCorrect: true,
                        ...(gatedFlowActive && { hasListened, hasRecorded }),
                        isHomeworkReady: computeHomeworkReady(finalSentence, undefined, pool.length)
                    });
                }
                
                // 숙제가 아닐 때만 초기화 (숙제인 경우 답변 그대로 유지)
                if (!isHomework) {
                    setTimeout(() => {
                        setShowFireworks(false);
                        setFlipStatus('idle');
                    }, 3000);
                }
            } else {
                // 숙제가 아닐 때만 피드백 표시
                if (!isHomework) {
                    setFlipStatus('fail');
                }
                
                // 응답 저장
                if (onResponse) {
                    onResponse({ 
                        ...existingResponse,
                        answer: finalSentence, 
                        isCorrect: false,
                        ...(gatedFlowActive && { hasListened, hasRecorded }),
                        isHomeworkReady: computeHomeworkReady(finalSentence, undefined, pool.length)
                    });
                }
                
                // 숙제인 경우 초기화하지 않고 그대로 유지
                if (!isHomework) {
                    setTimeout(() => {
                        resetGame();
                        setFlipStatus('idle');
                    }, 1500);
                }
            }
        }
    }, [pool, isReady, answer]);

    const resetGame = () => {
        const words = wordItems.map((item: { id: string; label: string }) => item.id);
        const shuffled = shuffleArray<string>(words);
        setPool(shuffled);
        setAnswer([]);
    };

    const getWordColor = (wordId: string, index: number) => {
        // 숙제인 경우 색상 표시하지 않음 (정답 여부 숨김)
        if (isHomework) {
            return '#4B5563'; // 기본 회색
        }
        
        // 학습인 경우 정답/오답 색상 표시
        if (correctWordIds[index] === wordId) {
            return '#10B981'; // 초록 (정답)
        } else {
            return '#EF4444'; // 빨강 (오답)
        }
    };

  return (
    <main>
      <h1 className="px-5 pt-5 heading1">{title}</h1>
      <div className="p-5 pt-3">
        <div>
          <div className={styles.gameCard}>
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt="" 
                loading="lazy"
                className="w-full max-w-[400px] max-h-[320px] rounded-3xl object-contain bg-white"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = DeskImg;
                }}
              />
            ) : (
              <div className="w-full max-w-[400px] h-[320px] rounded-3xl bg-gray-100 flex items-center justify-center text-gray-500 font-bold">
                이미지가 없습니다.
              </div>
            )}
            <ListenButton 
              size={48} 
              className="absolute top-4 right-4" 
              audioSrc={audioUrl} 
              onAudioEnded={() => {
                if (!gatedFlowActive) return;
                const nextHasListened = true;
                setHasListened(nextHasListened);
                if (onResponse) {
                  const currentSentence = convertIdsToSentence(answer);
                  onResponse({
                    ...existingResponse,
                    ...(gatedFlowActive && { hasListened: nextHasListened, hasRecorded }),
                    isHomeworkReady: computeHomeworkReady(currentSentence, undefined, pool.length)
                  });
                }
              }}
            />
          </div>
          {gatedFlowActive && !hasListened && (
            <p className="mt-2 text-sm text-center text-indigo-600">듣기를 완료하면 문제를 풀수 있습니다.</p>
          )}
          
          {recordingEnabled && (
            <div className={clsx('transition-opacity', (!canRecord && gatedFlowActive) && 'opacity-40 pointer-events-none')}>
              <RecordingBox 
                onRecordingComplete={handleRecordingComplete}
                existingAudioUrl={existingResponse?.recordingUrl || null}
              />
            </div>
          )}
          {gatedFlowActive && recordingEnabled && !canRecord && (
            <p className="mt-2 text-xs text-red-500">듣기를 완료한 후에 녹음을 진행해주세요.</p>
          )}
          <FlipCard
            flipStatus={flipStatus}
            front={
              <div 
                className={clsx(
                  'mt-3 min-h-32 flex flex-wrap justify-center gap-2 items-center p-4 rounded-lg bg-gray-50',
                  !allowArrangement && 'opacity-50'
                )}
              >
                {answer.length > 0 && answer.map((wordId, idx) => (
                  <button
                    key={`${wordId}-${idx}`}
                    onClick={() => allowArrangement && handleWordClick(wordId, false)}
                    disabled={!allowArrangement}
                    className={clsx(
                      dragStyles.wordDefault,
                      shakeWordId === wordId && dragStyles.wordShake,
                      !allowArrangement && 'cursor-not-allowed',
                      allowArrangement && 'cursor-pointer hover:opacity-80'
                    )}
                    style={{
                      color: getWordColor(wordId, idx),
                      backgroundColor: '#fff',
                    }}
                  >
                    {wordMap.get(wordId) || wordId}
                  </button>
                ))}
                {!answer.length && <div className='text-sm text-gray-400'>아래의 단어를 클릭하여 순서대로 조합하여 문장을 완성해주세요.</div>}
              </div>
            }
          />
        </div>
        <section>
          <div 
            className={clsx(
              'mt-3 min-h-32 flex flex-wrap justify-center gap-2 items-center p-4 rounded-lg bg-gray-50 cursor-pointer',
              !allowArrangement && 'opacity-50'
            )}
          >
            {pool.map((wordId, idx) => (
              <button
                key={`${wordId}-pool-${idx}`}
                onClick={() => allowArrangement && handleWordClick(wordId, true)}
                disabled={!allowArrangement}
                className={clsx(
                  dragStyles.wordDefault,
                  shakeWordId === wordId && dragStyles.wordShake,
                  !allowArrangement && 'cursor-not-allowed',
                  allowArrangement && 'cursor-pointer hover:opacity-90'
                )}
                style={{
                  color: isHomework ? '#4B5563' : '#1F2937',
                  backgroundColor: '#fff',
                  transition: 'transform 0.2s ease, opacity 0.2s ease',
                  transform: allowArrangement ? 'translateY(0)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (allowArrangement) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (allowArrangement) {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                {wordMap.get(wordId) || wordId}
              </button>
            ))}
          </div>
        </section>
        {gatedFlowActive && recordingEnabled && !hasRecorded && (
          <p className="mt-3 text-sm text-indigo-600">녹음을 완료하면 단어를 배열할 수 있습니다.</p>
        )}
        {showFireworks && <FireworksLayer />}
      </div>
    </main>
  )
}
